import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Compass, BookOpen, Users, Brain, ChevronRight } from 'lucide-react';

export default function OtherProgramsPage() {
  const programs = [
    {
      icon: Compass,
      title: "Cultural Exploration Program",
      description: "Journey through recreated ancient civilizations including Olmec, Mayan, and Kemet environments. Experience history firsthand through immersive VR tours.",
      features: ["Guided virtual tours", "Interactive historical exhibits", "Cultural storytelling experiences"]
    },
    {
      icon: BookOpen,
      title: "K-12 Educational VR",
      description: "Engaging virtual reality content designed specifically for K-12 educators and students. Bring lessons to life with immersive educational experiences.",
      features: ["Curriculum-aligned content", "Interactive learning modules", "Teacher resources and guides"]
    },
    {
      icon: Users,
      title: "Higher Education Partnerships",
      description: "Comprehensive VR solutions for universities and colleges looking to integrate immersive technology into their academic programs.",
      features: ["Custom VR environments", "Research collaboration tools", "Virtual lecture halls"]
    },
    {
      icon: Brain,
      title: "Neurodiverse Learning Support",
      description: "Specially designed programs supporting learners with diverse needs. Our VR experiences adapt to different learning styles and accessibility requirements.",
      features: ["Customizable sensory settings", "Flexible pacing options", "Supportive learning environments"]
    },
  ];

  return (
    <div className="min-h-screen pt-24" data-testid="other-programs-page">
      {/* Hero Section */}
      <section className="py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-testid="other-programs-title">
              Other Programs
            </h1>
            <p className="text-xl text-muted-foreground">
              Discover our range of specialized programs designed to serve diverse educational needs and communities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-8 pb-24">
        <div className="container-custom">
          <div className="space-y-8">
            {programs.map((program, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 rounded-2xl glass-light card-hover"
                data-testid={`program-${index}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-lg bg-primary/20 flex items-center justify-center">
                        <program.icon className="w-7 h-7 text-primary" />
                      </div>
                      <h3 className="text-2xl font-serif font-semibold text-white">{program.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed mb-4">{program.description}</p>
                  </div>
                  
                  <div className="bg-muted/30 rounded-xl p-6">
                    <h4 className="text-white font-semibold mb-4">Key Features</h4>
                    <ul className="space-y-3">
                      {program.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <ChevronRight className="w-4 h-4 text-primary" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <h2 className="text-2xl font-serif font-bold text-white mb-4">
              Interested in Our Programs?
            </h2>
            <p className="text-muted-foreground mb-6">
              Contact us to learn more about how our programs can serve your educational needs.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
              Get in Touch
              <ChevronRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
